/**
 * Cash Flow Chart Utilities
 *
 * Pure functions for computing cumulative ceiling, obligation, and outlay curves
 * from award portfolio and time series data.
 */
import type { AwardPortfolioItem, TimeSeriesPoint } from '@/types/usaspending'

export type ChartMode = 'cumulative' | 'monthly'

/** Convert a TimeSeriesPoint to a Date, using `date` field when available */
export function pointToDate(
  point: Pick<TimeSeriesPoint, 'year' | 'month' | 'date'>,
): Date {
  if (point.date) return new Date(point.date + 'T00:00:00')
  return new Date(point.year, point.month - 1, 1)
}

/**
 * Safely coerce any value to a finite number. Handles null, undefined, NaN,
 * Infinity, and string representations from Decimal serialization.
 */
export function safeNum(v: unknown): number {
  if (v == null) return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

// IDV type code → human label (mirrors IDV_TYPE_LABELS in idv-grouping.ts)
const IDV_LABELS: Record<string, string> = {
  IDV_E: 'BPA',
  IDV_A: 'GWAC',
  IDV_B: 'IDC',
  IDV_B_B: 'IDC',
  IDV_B_C: 'IDC',
  IDV_C: 'FSS',
  IDV_D: 'BOA',
}

export interface IdvFilterResult {
  /** Awards with IDV parent records removed */
  awards: AwardPortfolioItem[]
  /** Unique human-readable IDV type labels that were excluded (e.g. ["BPA", "GWAC"]) */
  excludedTypes: string[]
}

/**
 * Filter out IDV parent awards from a portfolio array for aggregate computations.
 *
 * IDV parents (category === 'idv') represent vehicle-level records whose
 * ceiling and obligation values overlap with (and dwarf) the child task
 * orders. Including them in aggregate ceiling/obligation/outlay sums
 * causes massive double-counting and chart-distorting spikes.
 *
 * Returns both the filtered array and the distinct IDV type labels that
 * were excluded so the UI can tell the user what was removed.
 */
export function filterIdvParents(
  awards: AwardPortfolioItem[],
): IdvFilterResult {
  const filtered: AwardPortfolioItem[] = []
  const typeSet = new Set<string>()

  for (const award of awards) {
    if (award.category === 'idv') {
      const label = (award.type && IDV_LABELS[award.type]) || 'IDV'
      typeSet.add(label)
    } else {
      filtered.push(award)
    }
  }

  return {
    awards: filtered,
    excludedTypes: [...typeSet].sort(),
  }
}

export interface CumulativePoint {
  x: Date
  y: number
}

export interface PerAwardCurves {
  ceiling: CumulativePoint[] | null
  obligation: CumulativePoint[]
  outlay: CumulativePoint[]
}

/**
 * Month key for bucketing: YYYYMM integer
 */
function monthKey(d: Date): number {
  return d.getFullYear() * 100 + (d.getMonth() + 1)
}

function monthKeyFromYM(year: number, month: number): number {
  return year * 100 + month
}

/**
 * Generate all months between start and end (inclusive) as Date objects (1st of month).
 */
export function generateMonthRange(start: Date, end: Date): Date[] {
  const months: Date[] = []
  const current = new Date(start.getFullYear(), start.getMonth(), 1)
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1)
  while (current <= endMonth) {
    months.push(new Date(current))
    current.setMonth(current.getMonth() + 1)
  }
  return months
}

export interface CeilingResult {
  curve: CumulativePoint[]
  preRange: number
}

/**
 * Compute aggregate ceiling curve: accumulate base_and_all_options_value by date_signed month.
 * Awards without a ceiling or date_signed are skipped.
 * Returns both the cumulative curve and the pre-range total so callers can
 * correctly decumulate without dumping the historical sum into month 1.
 */
export function computeAggregateCeiling(
  awards: AwardPortfolioItem[],
  startDate: Date,
  endDate: Date,
): CeilingResult {
  const byMonth = new Map<number, number>()

  for (const award of awards) {
    if (award.base_and_all_options_value == null || !award.date_signed) continue
    const signed = new Date(award.date_signed)
    const key = monthKey(signed)
    byMonth.set(
      key,
      (byMonth.get(key) ?? 0) + safeNum(award.base_and_all_options_value),
    )
  }

  // Accumulate ceiling from awards signed before the chart start date
  const startKey = monthKey(startDate)
  let preRange = 0
  for (const [key, value] of byMonth) {
    if (key < startKey) preRange += value
  }

  const months = generateMonthRange(startDate, endDate)
  let cumulative = preRange
  const curve = months.map((d) => {
    const key = monthKey(d)
    cumulative += byMonth.get(key) ?? 0
    return { x: d, y: cumulative }
  })

  return { curve, preRange }
}

/**
 * Compute cumulative obligation from time series data (running sum of monthly amounts).
 * @param initialValue - pre-range obligation to seed the cumulative sum (e.g. from awards
 *   whose obligation activity predates the time series range).
 */
export function computeCumulativeObligation(
  timeSeries: TimeSeriesPoint[],
  initialValue: number = 0,
): CumulativePoint[] {
  const sorted = [...timeSeries].sort(
    (a, b) =>
      (a.date ?? '').localeCompare(b.date ?? '') ||
      a.year - b.year ||
      a.month - b.month,
  )

  let cumulative = initialValue
  return sorted.map((point) => {
    cumulative += point.amount
    return {
      x: pointToDate(point),
      y: cumulative,
    }
  })
}

/**
 * Compute aggregate outlay curve estimated from award-level total_outlays.
 * Uses proportional distribution relative to the obligation curve:
 * at each time point, outlay = totalOutlay * (obligation_at_t / totalObligation).
 * Applies a lag effect (outlay trails obligation by a few months).
 */
export function computeAggregateOutlay(
  awards: AwardPortfolioItem[],
  obligationCurve: CumulativePoint[],
): CumulativePoint[] {
  if (obligationCurve.length === 0) return []

  const totalObligation = awards.reduce(
    (sum, a) => sum + safeNum(a.total_obligation),
    0,
  )
  const totalOutlay = awards.reduce(
    (sum, a) => sum + safeNum(a.total_outlays),
    0,
  )

  if (totalObligation === 0) {
    return obligationCurve.map((p) => ({ x: p.x, y: 0 }))
  }

  const outlayRatio = totalOutlay / totalObligation

  // Apply lag: outlay at month i uses obligation from ~3 months prior
  const LAG_MONTHS = 3
  return obligationCurve.map((point, i) => {
    const lagIdx = Math.max(0, i - LAG_MONTHS)
    return {
      x: point.x,
      y: obligationCurve[lagIdx].y * outlayRatio,
    }
  })
}

/**
 * Compute per-award cumulative curves for a single award view.
 */
export function computePerAwardCumulative(
  timeSeries: TimeSeriesPoint[],
  award: AwardPortfolioItem,
): PerAwardCurves {
  const obligation = computeCumulativeObligation(timeSeries)

  // Ceiling: flat line at base_and_all_options_value over the PoP
  let ceiling: CumulativePoint[] | null = null
  if (
    award.base_and_all_options_value != null &&
    award.period_of_performance_start_date &&
    award.period_of_performance_current_end_date
  ) {
    const popStart = new Date(award.period_of_performance_start_date)
    const popEnd = new Date(award.period_of_performance_current_end_date)
    const months = generateMonthRange(popStart, popEnd)
    ceiling = months.map((d) => ({
      x: d,
      y: safeNum(award.base_and_all_options_value),
    }))
  }

  // Outlay: estimated from total_outlays with lag
  const totalObl = safeNum(award.total_obligation)
  const totalOut = safeNum(award.total_outlays)
  const ratio = totalObl > 0 ? totalOut / totalObl : 0
  const LAG_MONTHS = 3
  const outlay = obligation.map((point, i) => {
    const lagIdx = Math.max(0, i - LAG_MONTHS)
    return {
      x: point.x,
      y: obligation[lagIdx].y * ratio,
    }
  })

  return { ceiling, obligation, outlay }
}

/**
 * Get Oct 1 dates (FY start) between start and end for FY boundary markers.
 */
export function getFiscalYearBoundaries(
  startDate: Date,
  endDate: Date,
): Date[] {
  const boundaries: Date[] = []
  // Start from the year of startDate
  let year = startDate.getFullYear()
  // If startDate is before Oct 1 of its year, first FY boundary could be Oct 1 of that year
  // If after, start from next year
  while (true) {
    const oct1 = new Date(year, 9, 1) // October 1
    if (oct1 > endDate) break
    if (oct1 >= startDate) {
      boundaries.push(oct1)
    }
    year++
  }
  return boundaries
}

/**
 * Build ECharts markLine data for FY boundaries (Oct 1 vertical lines).
 * Replaces the Chart.js-specific createFyBoundaryPlugin.
 */
export function buildFyMarkLines(
  startDate: Date,
  endDate: Date,
): {
  xAxis: number
  label: { formatter: string; fontSize: number; position: string }
  lineStyle: { color: string; type: string; width: number }
}[] {
  const boundaries = getFiscalYearBoundaries(startDate, endDate)
  // Thin labels when there are too many FY boundaries to avoid overlapping text.
  // All boundaries still get a dashed line; only every Nth gets a visible label.
  const step = boundaries.length > 20 ? 5 : boundaries.length > 10 ? 2 : 1
  return boundaries.map((date, i) => {
    const fy = date.getFullYear() + 1
    const showLabel = i % step === 0
    return {
      xAxis: date.getTime(),
      label: {
        formatter: showLabel ? `FY${fy}` : '',
        fontSize: 10,
        position: 'insideStartTop' as const,
      },
      lineStyle: {
        color: 'rgba(148, 163, 184, 0.2)',
        type: 'dashed',
        width: 1,
      },
    }
  })
}

/**
 * Build per-category cumulative obligation curves from grouped portfolio data
 * and time series. Splits aggregate obligation into contract, grant, and IDV
 * curves proportionally by each category's share of total obligation.
 *
 * No new API calls — derived entirely from already-loaded portfolio data.
 */
export function buildCategoryBreakdownCurves(
  grouped: {
    standaloneContracts: { total_obligation?: unknown }[]
    grants: { total_obligation?: unknown }[]
    idvGroups: { recipientObligation: number }[]
  },
  timeSeries: { year: number; month: number; amount: number }[],
): {
  contractCurve: CumulativePoint[]
  grantCurve: CumulativePoint[]
  idvCurve: CumulativePoint[]
} {
  // Compute category totals
  const contractTotal = grouped.standaloneContracts.reduce(
    (sum, a) => sum + safeNum(a.total_obligation),
    0,
  )
  const grantTotal = grouped.grants.reduce(
    (sum, a) => sum + safeNum(a.total_obligation),
    0,
  )
  const idvTotal = grouped.idvGroups.reduce(
    (sum, g) => sum + g.recipientObligation,
    0,
  )
  const total = contractTotal + grantTotal + idvTotal

  // Proportional split of the time series
  const contractRatio = total > 0 ? contractTotal / total : 0
  const grantRatio = total > 0 ? grantTotal / total : 0
  const idvRatio = total > 0 ? idvTotal / total : 0

  const sorted = [...timeSeries].sort(
    (a, b) =>
      ((a as TimeSeriesPoint).date ?? '').localeCompare(
        (b as TimeSeriesPoint).date ?? '',
      ) ||
      a.year - b.year ||
      a.month - b.month,
  )

  let cumContract = 0
  let cumGrant = 0
  let cumIdv = 0

  const contractCurve: CumulativePoint[] = []
  const grantCurve: CumulativePoint[] = []
  const idvCurve: CumulativePoint[] = []

  for (const point of sorted) {
    cumContract += point.amount * contractRatio
    cumGrant += point.amount * grantRatio
    cumIdv += point.amount * idvRatio

    const x = pointToDate(point as TimeSeriesPoint)
    contractCurve.push({ x, y: cumContract })
    grantCurve.push({ x, y: cumGrant })
    idvCurve.push({ x, y: cumIdv })
  }

  return { contractCurve, grantCurve, idvCurve }
}

/**
 * Format currency in abbreviated form: $1.2B, $4.5M, $450K, $800
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '\u2014'
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}K`
  return `${sign}$${abs.toFixed(0)}`
}

/**
 * Get the date range from time series data.
 */
export function getTimeSeriesDateRange(
  timeSeries: TimeSeriesPoint[],
): { start: Date; end: Date } | null {
  if (timeSeries.length === 0) return null
  const sorted = [...timeSeries].sort(
    (a, b) =>
      (a.date ?? '').localeCompare(b.date ?? '') ||
      monthKeyFromYM(a.year, a.month) - monthKeyFromYM(b.year, b.month),
  )
  const first = sorted[0]
  const last = sorted[sorted.length - 1]
  return {
    start: pointToDate(first),
    end: pointToDate(last),
  }
}

/**
 * Convert a cumulative curve to monthly deltas (non-cumulative).
 * @param baseValue - the cumulative value before the first point (e.g. pre-range total).
 *   Without this, the first point's delta would include the entire historical sum.
 */
export function decumulate(
  curve: CumulativePoint[],
  baseValue: number = 0,
): CumulativePoint[] {
  if (curve.length === 0) return []
  return curve.map((point, i) => ({
    x: point.x,
    y: i === 0 ? point.y - baseValue : point.y - curve[i - 1].y,
  }))
}

/**
 * Compute raw monthly obligation amounts from time series data (non-cumulative).
 * More direct than decumulating — returns the raw per-month amounts sorted by date.
 */
export function computeMonthlyObligation(
  timeSeries: TimeSeriesPoint[],
): CumulativePoint[] {
  const sorted = [...timeSeries].sort(
    (a, b) =>
      (a.date ?? '').localeCompare(b.date ?? '') ||
      a.year - b.year ||
      a.month - b.month,
  )
  return sorted.map((point) => ({
    x: pointToDate(point),
    y: point.amount,
  }))
}

/**
 * Compute monthly outlay directly from monthly obligation values.
 *
 * The cumulative outlay uses `outlay[i] = cumObl[max(0, i-lag)] * ratio` which
 * creates a flat plateau for the first `lag` points. Decumulating that plateau
 * produces [spike, 0, 0, 0, ...] instead of the correct [0, 0, 0, spike, ...].
 *
 * This function avoids that by computing monthly outlay as:
 *   monthlyOutlay[i] = monthlyObl[i - lag] * ratio  (0 when i < lag)
 */
export function computeMonthlyOutlay(
  monthlyObl: CumulativePoint[],
  outlayRatio: number,
  lag: number = 3,
): CumulativePoint[] {
  return monthlyObl.map((point, i) => ({
    x: point.x,
    y: i >= lag ? monthlyObl[i - lag].y * outlayRatio : 0,
  }))
}

/**
 * FY2022 mandatory outlay reporting start (Oct 1, 2021).
 * Before this date, File C data may be incomplete.
 */
export const OUTLAY_REPORTING_START = new Date(2021, 9, 1)

/**
 * Format a date string (ISO or similar) as "MMM 'YY" (e.g., "Oct '21").
 */
export function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return dateStr
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  return `${months[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`
}

/**
 * Shared pre-reporting scale computation for outlay hybrid curves.
 *
 * Derives a `preScale` factor that, when applied to lagged cumulative obligation,
 * approximates the cumulative outlay just before the DATA Act reporting transition.
 * Also returns the `transitionSeed` used to anchor the first real data point.
 *
 * Returns `{ preScale: null, transitionSeed: null }` when insufficient data.
 */
function computePreScale(
  transitionKey: number,
  cumulativeObligation: CumulativePoint[],
  outlayRatio: number,
  preReportingOutlay: number | null,
): { preScale: number | null; transitionSeed: number | null } {
  const LAG_MONTHS = 3

  let transitionSeed: number | null = null
  if (preReportingOutlay != null && Number.isFinite(preReportingOutlay)) {
    transitionSeed = preReportingOutlay
  }

  let anchorBasis = 0
  let anchorIdx = -1
  for (let i = 0; i < cumulativeObligation.length; i++) {
    if (monthKey(cumulativeObligation[i].x) < transitionKey) anchorIdx = i
  }

  if (anchorIdx >= 0) {
    const lagAnchorIdx = Math.max(0, anchorIdx - LAG_MONTHS)
    const laggedAnchor = cumulativeObligation[lagAnchorIdx].y
    const unlaggedAnchor = cumulativeObligation[anchorIdx].y
    anchorBasis = laggedAnchor > 0 ? laggedAnchor : unlaggedAnchor

    const normalizedRatio = Number.isFinite(outlayRatio)
      ? Math.max(0, Math.min(1, outlayRatio))
      : 0
    const modeledSeed = anchorBasis * normalizedRatio
    transitionSeed =
      transitionSeed != null
        ? Math.max(transitionSeed, modeledSeed)
        : modeledSeed
  }

  if (transitionSeed == null || !Number.isFinite(transitionSeed)) {
    return { preScale: null, transitionSeed: null }
  }

  let preScale: number | null
  if (anchorBasis > 0) {
    preScale = transitionSeed / anchorBasis
    // Cap at 1.0: estimated outlay should never exceed obligation.
    // Without this, recipients with large pre-FY2022 outlay relative to
    // their transition-point obligation get wildly inflated estimates.
    preScale = Math.min(preScale, 1.0)
  } else {
    preScale = Number.isFinite(outlayRatio) ? Math.max(0, outlayRatio) : 0
  }

  if (!Number.isFinite(preScale)) {
    preScale = null
  }

  return { preScale, transitionSeed }
}

/**
 * Compute cumulative outlay curve from real File C monthly deltas.
 *
 * Produces a hybrid curve over the full obligation date range:
 * - Pre-reporting months (before earliest outlay TS data): estimated proportional
 *   curve using lagged obligation, scaled either by outlayRatio (fallback) or a
 *   portfolio-derived pre-reporting outlay seed.
 * - Post-reporting months (from earliest outlay onward): real cumulative from
 *   File C deltas, anchored to the derived pre-reporting seed at transition.
 *
 * Anchoring to a consistent transition seed keeps the curve continuous at
 * DATA Act cutover while still using real File C deltas after cutover.
 */
export function computeRealOutlayCurve(
  outlayTimeSeries: TimeSeriesPoint[],
  obligationCurve: CumulativePoint[],
  outlayRatio: number = 0,
  preReportingOutlay: number | null = null,
): CumulativePoint[] {
  if (outlayTimeSeries.length === 0 || obligationCurve.length === 0) return []

  const LAG_MONTHS = 3

  // Build lookup of outlay deltas by month key
  const outlayByMonth = new Map<number, number>()
  for (const point of outlayTimeSeries) {
    const key = monthKeyFromYM(point.year, point.month)
    outlayByMonth.set(key, (outlayByMonth.get(key) ?? 0) + point.amount)
  }

  // Find earliest outlay TS month — the transition point from estimated to real
  let earliestOutlayKey = Infinity
  for (const key of outlayByMonth.keys()) {
    if (key < earliestOutlayKey) earliestOutlayKey = key
  }
  const reportingStartKey = monthKey(OUTLAY_REPORTING_START)
  const transitionKey = Math.max(earliestOutlayKey, reportingStartKey)

  const { preScale, transitionSeed } = computePreScale(
    transitionKey,
    obligationCurve,
    outlayRatio,
    preReportingOutlay,
  )

  // Build hybrid curve over all obligation curve months
  let realCumulative: number | null = null

  return obligationCurve.map((point, i) => {
    const key = monthKey(point.x)
    const lagIdx = Math.max(0, i - LAG_MONTHS)
    const estimatedValue =
      preScale != null
        ? obligationCurve[lagIdx].y * preScale
        : obligationCurve[lagIdx].y * outlayRatio

    if (key < transitionKey) {
      // Pre-reporting: estimated from lagged obligation
      return { x: point.x, y: estimatedValue }
    }

    // Transition month: anchor real cumulative to estimated value for continuity.
    if (realCumulative == null) {
      realCumulative =
        transitionSeed != null && Number.isFinite(transitionSeed)
          ? transitionSeed
          : estimatedValue
    }

    // Post-reporting: add real File C deltas on top of anchored cumulative.
    realCumulative += outlayByMonth.get(key) ?? 0
    return { x: point.x, y: realCumulative }
  })
}

/**
 * Forward-fill a sparse cumulative curve onto a dense time axis.
 *
 * Children in an IDV group have sparse time series that may not share the same
 * date range as the combined axis. This function aligns a child curve to the
 * combined time axis, carrying the last known value forward through gaps so
 * ECharts stacking works correctly.
 *
 * @param curve  - sparse cumulative curve (e.g., one child TO's obligation)
 * @param axis   - dense time axis (derived from the combined time series)
 */
export function forwardFillCurve(
  curve: CumulativePoint[],
  axis: Date[],
): CumulativePoint[] {
  const byTime = new Map(curve.map((p) => [p.x.getTime(), p.y]))
  let last = 0
  return axis.map((d) => {
    const v = byTime.get(d.getTime())
    if (v !== undefined) last = v
    return { x: d, y: last }
  })
}

/**
 * Compute monthly outlay from real File C data.
 *
 * Hybrid approach matching computeRealOutlayCurve:
 * - Pre-reporting months: estimated monthly outlay (lagged obligation, scaled)
 * - Post-reporting months: real File C monthly deltas
 */
export function computeRealMonthlyOutlay(
  outlayTimeSeries: TimeSeriesPoint[],
  monthlyObligation: CumulativePoint[],
  outlayRatio: number = 0,
  preReportingOutlay: number | null = null,
): CumulativePoint[] {
  if (outlayTimeSeries.length === 0) return []

  const LAG_MONTHS = 3

  const outlayByMonth = new Map<number, number>()
  for (const point of outlayTimeSeries) {
    const key = monthKeyFromYM(point.year, point.month)
    outlayByMonth.set(key, (outlayByMonth.get(key) ?? 0) + point.amount)
  }

  // Find earliest outlay TS month — transition from estimated to real
  let earliestOutlayKey = Infinity
  for (const key of outlayByMonth.keys()) {
    if (key < earliestOutlayKey) earliestOutlayKey = key
  }
  const reportingStartKey = monthKey(OUTLAY_REPORTING_START)
  const transitionKey = Math.max(earliestOutlayKey, reportingStartKey)

  // Build cumulative values needed for scale computation
  let runningObligation = 0
  const cumulativeOblForScale = monthlyObligation.map((p) => {
    runningObligation += p.y
    return { x: p.x, y: runningObligation }
  })
  const { preScale } = computePreScale(
    transitionKey,
    cumulativeOblForScale,
    outlayRatio,
    preReportingOutlay,
  )

  return monthlyObligation.map((point, i) => {
    const key = monthKey(point.x)
    if (key < transitionKey) {
      // Pre-reporting: estimated = lagged monthly obligation × ratio
      return {
        x: point.x,
        y:
          i >= LAG_MONTHS
            ? monthlyObligation[i - LAG_MONTHS].y * (preScale ?? outlayRatio)
            : 0,
      }
    }
    // Post-reporting: real File C delta
    return { x: point.x, y: outlayByMonth.get(key) ?? 0 }
  })
}
